import { useEffect, useRef } from 'react';
import { HERO_CONFIG } from '../config/constants';

const { VIDEOS } = HERO_CONFIG;

/**
 * AbortControllerを使用した動画読み込み管理フック
 * @param {boolean} inView - ビューポートに表示されているか
 * @param {Object} heroState - ヒーロー状態
 * @param {Function} updateHeroState - ヒーロー状態更新関数
 * @param {Object} videoRefs - 動画要素のref
 * @param {boolean} isSlowConnection - 低速ネットワークかどうか
 */
export const useVideoLoader = (
  inView,
  heroState,
  updateHeroState,
  videoRefs,
  isSlowConnection
) => {
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!inView || heroState.loadingVideos || heroState.videosLoaded) {
      return;
    }

    // 低速ネットワークの場合は動画をスキップして画像スライドショーを有効化
    if (isSlowConnection) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Slow connection detected, skipping video loading');
      }
      updateHeroState({
        videosLoaded: true,
        firstVideoReady: true,
        videoErrors: VIDEOS.map(() => true), // 全ての動画でエラーとして扱う
      });
      return;
    }

    // 前のAbortControllerをクリーンアップ
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    updateHeroState({ loadingVideos: true });

    const loadVideos = async () => {
      try {
        // AbortController のシグナルでキャンセルされたかチェック
        if (signal.aborted) return;

        // 最初の動画を優先的に読み込み
        const loadFirstVideo = async () => {
          if (signal.aborted) throw new Error('Aborted');

          const firstVideo = videoRefs.current[0].current;
          if (!firstVideo) {
            throw new Error('First video element not found');
          }

          if (process.env.NODE_ENV === 'development') {
            console.log('First video loading started:', VIDEOS[0]);
          }
          firstVideo.src = VIDEOS[0];

          return new Promise((resolve, reject) => {
            if (signal.aborted) {
              reject(new Error('Aborted'));
              return;
            }

            let timeoutId;

            const cleanup = () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              firstVideo.removeEventListener(
                'canplaythrough',
                handleCanPlayThrough
              );
              firstVideo.removeEventListener('error', handleError);
            };

            const handleCanPlayThrough = () => {
              cleanup();
              if (signal.aborted) return;

              if (process.env.NODE_ENV === 'development') {
                console.log('First video can play through');
              }
              updateHeroState({ firstVideoReady: true });
              resolve();
            };

            const handleError = error => {
              cleanup();
              if (signal.aborted) return;

              if (process.env.NODE_ENV === 'development') {
                console.error('First video loading error:', error.message);
              }
              // エラーが発生しても続行
              updateHeroState({
                firstVideoReady: true,
                videoErrors: prev => {
                  const newErrors = [...prev];
                  newErrors[0] = true;
                  return newErrors;
                },
              });
              resolve();
            };

            const handleTimeout = () => {
              cleanup();
              if (signal.aborted) return;

              if (process.env.NODE_ENV === 'development') {
                console.warn('First video loading timeout');
              }
              // タイムアウトが発生しても続行
              updateHeroState({
                firstVideoReady: true,
                videoErrors: prev => {
                  const newErrors = [...prev];
                  newErrors[0] = true;
                  return newErrors;
                },
              });
              resolve();
            };

            // AbortSignalのリスナーを追加
            const handleAbort = () => {
              cleanup();
              reject(new Error('Aborted'));
            };

            signal.addEventListener('abort', handleAbort, { once: true });

            // 10秒でタイムアウト
            timeoutId = setTimeout(handleTimeout, 10000);

            firstVideo.addEventListener(
              'canplaythrough',
              handleCanPlayThrough,
              {
                once: true,
              }
            );
            firstVideo.addEventListener('error', handleError, { once: true });
            firstVideo.load();
          });
        };

        // 残りの動画をバックグラウンドで読み込み
        const loadRemainingVideos = async () => {
          if (VIDEOS.length <= 1 || signal.aborted) return;

          const remainingPromises = VIDEOS.slice(1).map(
            (videoSrc, originalIndex) => {
              const index = originalIndex + 1;
              return new Promise(resolve => {
                if (signal.aborted) {
                  resolve();
                  return;
                }

                const video = videoRefs.current[index].current;
                if (!video) {
                  resolve();
                  return;
                }

                if (process.env.NODE_ENV === 'development') {
                  console.log(
                    `Video ${index + 1} loading started: ${videoSrc}`
                  );
                }
                video.src = videoSrc;

                let timeoutId;

                const cleanup = () => {
                  if (timeoutId) {
                    clearTimeout(timeoutId);
                  }
                  video.removeEventListener(
                    'canplaythrough',
                    handleCanPlayThroughRemaining
                  );
                  video.removeEventListener('error', handleErrorRemaining);
                };

                const handleCanPlayThroughRemaining = () => {
                  cleanup();
                  if (signal.aborted) return;

                  if (process.env.NODE_ENV === 'development') {
                    console.log(`Video ${index + 1} can play through`);
                  }
                  resolve();
                };

                const handleErrorRemaining = () => {
                  cleanup();
                  if (signal.aborted) return;

                  if (process.env.NODE_ENV === 'development') {
                    console.error(`Video ${index + 1} loading error`);
                  }
                  // エラーを記録
                  updateHeroState(prev => {
                    const newErrors = [...prev.videoErrors];
                    newErrors[index] = true;
                    return { videoErrors: newErrors };
                  });
                  resolve();
                };

                const handleTimeoutRemaining = () => {
                  cleanup();
                  if (signal.aborted) return;

                  if (process.env.NODE_ENV === 'development') {
                    console.warn(`Video ${index + 1} loading timeout`);
                  }
                  // タイムアウトを記録
                  updateHeroState(prev => {
                    const newErrors = [...prev.videoErrors];
                    newErrors[index] = true;
                    return { videoErrors: newErrors };
                  });
                  resolve();
                };

                const handleAbort = () => {
                  cleanup();
                  resolve();
                };

                signal.addEventListener('abort', handleAbort, { once: true });

                // 15秒でタイムアウト
                timeoutId = setTimeout(handleTimeoutRemaining, 15000);

                video.addEventListener(
                  'canplaythrough',
                  handleCanPlayThroughRemaining,
                  { once: true }
                );
                video.addEventListener('error', handleErrorRemaining, {
                  once: true,
                });
                video.load();
              });
            }
          );

          await Promise.all(remainingPromises);
          if (!signal.aborted && process.env.NODE_ENV === 'development') {
            console.log('All remaining videos loaded');
          }
        };

        await loadFirstVideo();

        if (!signal.aborted) {
          loadRemainingVideos()
            .then(() => {
              if (!signal.aborted) {
                if (process.env.NODE_ENV === 'development') {
                  console.log('All videos loaded successfully');
                }
                updateHeroState({ videosLoaded: true });
              }
            })
            .catch(() => {
              if (!signal.aborted) {
                updateHeroState({ videosLoaded: true });
              }
            });
        }
      } catch (error) {
        if (signal.aborted) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Video loading was aborted');
          }
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('動画の読み込みエラー:', error.message);
        }
        updateHeroState({
          firstVideoReady: true,
          videosLoaded: true,
        });
      } finally {
        if (!signal.aborted) {
          updateHeroState({ loadingVideos: false });
          if (process.env.NODE_ENV === 'development') {
            console.log('動画読み込み処理が完了しました');
          }
        }
      }
    };

    loadVideos();

    return () => {
      // AbortControllerでキャンセル
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [inView, updateHeroState, isSlowConnection, videoRefs]); // eslint-disable-line react-hooks/exhaustive-deps

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
};
